import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Heart, UserPlus, MapPin } from "lucide-react";
import { api } from "../lib/api";
import { FourStars } from "./FourStars";

const SEEN_PREFIX = "afb_smart_popup_";

// Sequenced smart popup: news (15s) → donation (60s) → membership (130s)
// Each shown at most once per session.
const SCHEDULE = [
  { type: "news", delayMs: 15000 },
  { type: "donation", delayMs: 60000 },
  { type: "membership", delayMs: 130000 },
];

export const SmartPopups = () => {
  const [news, setNews] = useState(null);
  const [active, setActive] = useState(null); // { type, ...content }
  const fired = useRef(new Set());

  useEffect(() => {
    api.get("/news").then((r) => setNews(r.data?.[0] || null)).catch(() => {});
  }, []);

  useEffect(() => {
    const timers = SCHEDULE.map(({ type, delayMs }) => {
      return setTimeout(() => {
        // Fire only if not seen in this session and no popup currently active
        if (sessionStorage.getItem(SEEN_PREFIX + type)) return;
        if (fired.current.has(type)) return;
        fired.current.add(type);
        sessionStorage.setItem(SEEN_PREFIX + type, "1");
        setActive((curr) => curr || { type });
      }, delayMs);
    });
    return () => timers.forEach((t) => clearTimeout(t));
  }, []);

  const close = () => setActive(null);

  if (!active) return null;

  const renderContent = () => {
    if (active.type === "news") {
      if (!news) return null;
      return (
        <div className="grid grid-cols-1 md:grid-cols-5">
          <div className="md:col-span-2 relative">
            <img src={news.image_url} alt={news.title} className="w-full h-44 md:h-full object-cover" />
            <div className="absolute top-3 left-3 bg-[#CE1126] text-white text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-sm font-semibold">À la une</div>
          </div>
          <div className="md:col-span-3 p-7 md:p-8">
            <div className="flex items-center gap-3 mb-3">
              <FourStars size={11} />
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] font-semibold">{news.category}</span>
            </div>
            <h2 className="font-display text-xl md:text-2xl text-[#0A0F1A] leading-tight">{news.title}</h2>
            <p className="text-sm text-[#475569] mt-3 leading-relaxed line-clamp-3">{news.summary}</p>
            <div className="flex items-center gap-3 mt-6">
              <Link to={`/actualites/${news.id}`} onClick={close} data-testid="popup-news-cta" className="inline-flex items-center gap-2 bg-[#0052CC] hover:bg-[#003D99] text-white px-5 py-2.5 rounded-sm text-sm font-semibold transition-colors">
                Lire l'actualité <ArrowRight size={15} />
              </Link>
              <button onClick={close} className="text-sm text-[#475569] hover:text-[#0A0F1A]">Plus tard</button>
            </div>
          </div>
        </div>
      );
    }
    if (active.type === "donation") {
      return (
        <div className="p-8 md:p-10 text-center bg-gradient-to-br from-white via-[#D4AF37]/5 to-white">
          <div className="inline-flex w-16 h-16 rounded-full bg-[#D4AF37]/15 items-center justify-center mb-5">
            <Heart size={28} className="text-[#D4AF37]" fill="#D4AF37" />
          </div>
          <FourStars size={11} className="justify-center mb-3" />
          <h2 className="font-display text-2xl md:text-3xl text-[#0A0F1A] leading-tight">Soutenez le mouvement</h2>
          <p className="text-sm md:text-base text-[#475569] mt-3 leading-relaxed max-w-md mx-auto">
            Chaque don finance des actions concrètes : éducation, santé, soutien à l'Est, mobilisation citoyenne. Votre contribution compte.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3 mt-7">
            <Link to="/dons" onClick={close} data-testid="popup-donation-cta" className="cta-glow-gold rounded-sm px-7 py-3.5 text-sm font-bold tracking-wider uppercase inline-flex items-center justify-center gap-2">
              <Heart size={16} /> Faire un don
            </Link>
            <button onClick={close} className="text-sm text-[#475569] hover:text-[#0A0F1A] underline underline-offset-2">Plus tard</button>
          </div>
        </div>
      );
    }
    if (active.type === "membership") {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2">
          <div className="bg-gradient-to-br from-[#0052CC] to-[#0A0F1A] p-8 md:p-10 text-white relative overflow-hidden">
            <div className="absolute inset-0 stars-pattern opacity-15" />
            <div className="relative">
              <FourStars size={12} className="mb-4" />
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-3">Mouvement national</div>
              <h2 className="font-display text-2xl md:text-3xl leading-tight">Rejoignez les A.F.B.</h2>
              <p className="text-white/85 text-sm mt-4 leading-relaxed">
                Inscrivez-vous gratuitement et accédez à votre espace membre : cotisations, sondages, messagerie, sections territoriales.
              </p>
            </div>
          </div>
          <div className="p-8 md:p-10 flex flex-col justify-center">
            <ul className="space-y-3 text-sm text-[#0A0F1A]">
              <li className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#D4AF37]" /> Espace membre sécurisé</li>
              <li className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#D4AF37]" /> Vote aux sondages & élections</li>
              <li className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#D4AF37]" /> Messagerie interne</li>
              <li className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#D4AF37]" /> Section locale ou diaspora</li>
            </ul>
            <div className="flex flex-col sm:flex-row gap-3 mt-7">
              <Link to="/register" onClick={close} data-testid="popup-membership-cta" className="cta-glow-blue rounded-sm px-6 py-3.5 text-sm font-bold tracking-wider uppercase inline-flex items-center justify-center gap-2">
                <UserPlus size={16} /> Devenir membre
              </Link>
              <Link to="/carte" onClick={close} className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-sm border border-[#0052CC]/30 text-[#0052CC] hover:bg-[#0052CC]/5 text-sm font-semibold">
                <MapPin size={16} /> Voir la carte
              </Link>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <AnimatePresence>
      {active && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60]" onClick={close}
            data-testid={`popup-${active.type}-backdrop`}
          />
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[61] flex items-center justify-center p-4 pointer-events-none"
            data-testid={`popup-${active.type}`}
          >
            <div className="relative pointer-events-auto w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden border-2 border-[#D4AF37]">
              <button
                onClick={close}
                aria-label="Fermer"
                data-testid={`popup-${active.type}-close`}
                className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center"
              >
                <X size={18} />
              </button>
              {renderContent()}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SmartPopups;
